import { STATIC_RELEASE_MANIFEST_URL, type StaticReleaseManifest } from '../release/staticRelease';
import {
  validateAssetDescriptor, type AssetDescriptor, type AssetLease, type AssetLocation,
  type AssetRequestOptions, type AssetResolver, type AssetAcquisition,
} from './AssetContracts';
import { AssetLoader, assertLocationValid } from './AssetLoader';
import { BrowserAssetStore } from './BrowserAssetStore';

interface ResourceSource {
  readonly descriptor: AssetDescriptor;
  readonly resolver: AssetResolver;
}

/** Application acquisition boundary. Signed URLs are capabilities, never cache identities. */
export class GameAssetResources {
  readonly #sources = new Map<string, ResourceSource>();
  readonly #descriptors = new Map<string, Promise<AssetDescriptor>>();
  #privateInvalidation: BroadcastChannel | null = null;
  #manifest: Promise<ReadonlyMap<string, ResourceSource>> | null = null;
  #started = false;
  #loader: AssetAcquisition;

  constructor(loader: AssetAcquisition = new AssetLoader(new BrowserAssetStore()), private readonly request: typeof fetch = (...args) => fetch(...args),
    private readonly apiBaseUrl = (import.meta.env?.VITE_APP_SERVER_URL as string | undefined)?.trim() ?? '') { this.#loader = loader; }

  get loader(): AssetAcquisition { this.#started = true; return this.#loader; }

  /** Install a host adapter once during bootstrap, before any resource acquisition. */
  configureAcquisition(loader: AssetAcquisition): void {
    if (this.#started) throw new Error('Asset acquisition is already in use.');
    this.#loader.dispose();
    this.#loader = loader;
  }

  async acquire(url: string, options: AssetRequestOptions = {}): Promise<AssetLease> {
    options.signal?.throwIfAborted();
    const registered = this.#sources.get(url);
    if (registered) return this.loader.acquire(registered.descriptor, registered.resolver, options);
    if (url.startsWith('/__private_assets__/')) throw new Error('Private asset registration is no longer authorized.');
    const parsed = new URL(url, globalThis.location?.href ?? 'http://localhost/');
    const browserOrigin = globalThis.location?.origin ?? 'http://localhost';
    const sameOrigin = parsed.origin === browserOrigin;
    const apiOrigin = new URL(this.apiBaseUrl || browserOrigin, browserOrigin).origin;
    if ((sameOrigin || parsed.origin === apiOrigin) && /^\/api\/media\/(assets|revisions)\/[^/]+$/.test(parsed.pathname)) {
      const base = this.apiBaseUrl || (sameOrigin ? '' : parsed.origin);
      const endpoint = `${base}${parsed.pathname}/descriptor`;
      const immutable = parsed.pathname.startsWith('/api/media/revisions/');
      let pending = immutable ? this.#descriptors.get(endpoint) : undefined;
      if (!pending) {
        pending = this.request(endpoint, { signal: AbortSignal.timeout(10_000), cache: immutable ? 'default' : 'no-cache' })
          .then(async response => {
            if (!response.ok) throw new Error(`Asset descriptor request failed with HTTP ${response.status}.`);
            const body = await response.json() as { descriptor?: unknown };
            validateAssetDescriptor(body.descriptor);
            return body.descriptor;
          }).catch(error => { this.#descriptors.delete(endpoint); throw error; });
        if (immutable) {
          this.#descriptors.set(endpoint, pending);
          while (this.#descriptors.size > 512) this.#descriptors.delete(this.#descriptors.keys().next().value!);
        }
      }
      const descriptor = await pending;
      options.signal?.throwIfAborted();
      return this.loader.acquire(descriptor, {
        resolve: async (resolved, signal) => {
          const result = await this.request(`${base}/api/media/revisions/${encodeURIComponent(resolved.id)}/resolve`,
            { method: 'POST', signal, cache: 'no-store' });
          if (!result.ok) throw new Error(`Asset resolution failed with HTTP ${result.status}.`);
          const location = await result.json() as AssetLocation;
          // Relative locations belong to the API host, not the document host.
          return { ...location, url: new URL(location.url, new URL(base || browserOrigin, browserOrigin)).href };
        },
      }, options);
    }
    if (sameOrigin && (parsed.pathname.startsWith('/assets/') || /^\/releases\/[^/]+\/assets\//.test(parsed.pathname))) {
      const sources = await this.#staticSources();
      options.signal?.throwIfAborted();
      const source = sources.get(parsed.pathname);
      if (source) return this.loader.acquire(source.descriptor, source.resolver, options);
    }
    return this.loader.acquireUnverified({ url }, options);
  }

  /** Register a freshly authorized private/signed source; the resolver reauthorizes every acquisition. */
  register(url: string, descriptor: AssetDescriptor, resolver: AssetResolver): void {
    validateAssetDescriptor(descriptor);
    if (descriptor.access.kind === 'private') this.#listenForInvalidation();
    this.#sources.set(url, { descriptor, resolver });
    // Registrations carry no bytes and are bounded independently of the persistent cache.
    while (this.#sources.size > 512) this.#sources.delete(this.#sources.keys().next().value!);
  }

  async purgePrivateScope(scope?: string): Promise<void> {
    this.#listenForInvalidation();
    this.#privateInvalidation?.postMessage({ scope: scope ?? null });
    await this.#purgePrivateScope(scope);
  }

  async #purgePrivateScope(scope?: string): Promise<void> {
    for (const [url, source] of this.#sources) {
      if (source.descriptor.access.kind === 'private' && (scope === undefined || source.descriptor.access.scope === scope)) {
        this.#sources.delete(url);
      }
    }
    await this.loader.purgePrivateScope(scope);
  }

  #listenForInvalidation(): void {
    if (this.#privateInvalidation || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
    this.#privateInvalidation = new BroadcastChannel('rl-web-private-asset-invalidation');
    this.#privateInvalidation.onmessage = event => {
      const value: unknown = event.data;
      if (!value || typeof value !== 'object' || !('scope' in value)) return;
      if (value.scope !== null && typeof value.scope !== 'string') return;
      void this.#purgePrivateScope(value.scope ?? undefined).catch(() => undefined);
    };
  }

  dispose(): void {
    this.#privateInvalidation?.close();
    this.#privateInvalidation = null;
    this.#sources.clear();
    this.#descriptors.clear();
    this.#loader.dispose();
  }

  #staticSources(): Promise<ReadonlyMap<string, ResourceSource>> {
    this.#manifest ??= this.request(STATIC_RELEASE_MANIFEST_URL, { cache: 'no-cache' }).then(async response => {
      if (!response.ok) throw new Error(`Static asset manifest failed with HTTP ${response.status}.`);
      const manifest = await response.json() as StaticReleaseManifest;
      if (manifest.schemaVersion !== 1 || !Array.isArray(manifest.assets)) throw new Error('Invalid static asset manifest.');
      const entries = new Map(manifest.assets.map(entry => [entry.id, entry]));
      const sources = new Map<string, ResourceSource>();
      const resolving = new Set<string>();
      const resolve = (id: string): ResourceSource => {
        const existing = sources.get(id);
        if (existing) return existing;
        const entry = entries.get(id);
        if (!entry || resolving.has(id)) throw new Error('Missing or cyclic static asset dependency.');
        resolving.add(id);
        const descriptor: AssetDescriptor = { id, revision: entry.sha256, sha256: entry.sha256,
          byteLength: entry.byteLength, mimeType: entry.mimeType, access: { kind: 'public' },
          dependencies: entry.dependencies.map((dependency: string) => resolve(dependency).descriptor) };
        validateAssetDescriptor(descriptor);
        const location = { url: entry.url };
        assertLocationValid(location);
        const source = { descriptor, resolver: { resolve: async () => location } };
        sources.set(id, source);
        sources.set(entry.url, source);
        resolving.delete(id);
        return source;
      };
      for (const id of entries.keys()) resolve(id);
      return sources;
    }).catch(error => { this.#manifest = null; throw error; });
    return this.#manifest;
  }
}

export const gameAssetResources = new GameAssetResources();
